/**
 * readinessScoring.js — Shared readiness score engine
 *
 * Single source of truth for score calculation. Used by:
 *  - loanApplicationSlice.js (Redux reducer)
 *  - ReadinessBreakdown.jsx (explainability UI)
 *
 * Returns both the total score AND a detailed breakdown per category
 * with evidence items and improvement suggestions.
 */

// ── Category definitions ────────────────────────────────────────
export const CATEGORIES = [
  { id: 'income',    label: 'Income Readiness',       maxPoints: 25, icon: 'ti-briefcase',     color: '#0d6efd' },
  { id: 'assets',    label: 'Asset & Cash Readiness',  maxPoints: 20, icon: 'ti-wallet',        color: '#198754' },
  { id: 'credit',    label: 'Credit Readiness',        maxPoints: 20, icon: 'ti-shield-check',  color: '#6f42c1' },
  { id: 'structure', label: 'Loan Structure Fit',      maxPoints: 15, icon: 'ti-home',          color: '#fd7e14' },
  { id: 'docs',      label: 'Documentation Strength',  maxPoints: 10, icon: 'ti-file-text',     color: '#20c997' },
  { id: 'intent',    label: 'Market Timing & Intent',  maxPoints: 10, icon: 'ti-target',        color: '#e83e8c' },
];

/**
 * Calculate the full readiness breakdown.
 *
 * @param {Object} state — the loanApplication Redux state (or equivalent shape)
 * @returns {{ total: number, categories: Array<CategoryResult> }}
 *
 * Each CategoryResult:
 *   { id, label, maxPoints, points, icon, color, evidence: string[], improvements: string[] }
 */
export function calculateReadinessBreakdown(state) {
  const appData = state.applicationData || {};
  const prodRec = state.productRecommendation || {};
  const results = [];

  // ── 1. INCOME READINESS (0-25) ──────────────────────────────
  {
    let points = 0;
    const evidence = [];
    const improvements = [];
    const employmentData = appData.employmentIncome || {};
    const otherIncomeData = appData.otherIncome || {};

    if (employmentData.entries?.length > 0) {
      points += 10;
      evidence.push(`${employmentData.entries.length} employment record${employmentData.entries.length > 1 ? 's' : ''} provided`);
    } else {
      improvements.push({ text: 'Add employment history', impact: 10 });
    }

    if (otherIncomeData.entries?.length > 0) {
      points += 5;
      evidence.push('Additional income sources documented');
    } else {
      improvements.push({ text: 'Document other income sources (rental, investments, etc.)', impact: 5 });
    }

    const income = parseFloat(prodRec.annualIncome);
    const debts = parseFloat(prodRec.monthlyDebts);
    if (income && debts) {
      const dti = (debts * 12) / income;
      if (dti < 0.36) {
        points += 10;
        evidence.push(`DTI ratio: ${(dti * 100).toFixed(1)}% — Excellent`);
      } else if (dti < 0.43) {
        points += 7;
        evidence.push(`DTI ratio: ${(dti * 100).toFixed(1)}% — Good`);
        improvements.push({ text: 'Reducing monthly debts could improve your DTI', impact: 3 });
      } else if (dti < 0.50) {
        points += 4;
        evidence.push(`DTI ratio: ${(dti * 100).toFixed(1)}% — Moderate`);
        improvements.push({ text: 'Paying down debt would significantly improve readiness', impact: 6 });
      } else {
        evidence.push(`DTI ratio: ${(dti * 100).toFixed(1)}% — High`);
        improvements.push({ text: 'DTI is above 50% — consider debt reduction strategies', impact: 10 });
      }
    } else if (income) {
      points += 5;
      evidence.push(`Annual income: $${income.toLocaleString()}`);
      improvements.push({ text: 'Provide monthly debt payments to calculate DTI', impact: 5 });
    } else {
      improvements.push({ text: 'Provide annual income', impact: 5 });
    }

    results.push({
      ...CATEGORIES[0],
      points: Math.min(points, 25),
      evidence,
      improvements,
    });
  }

  // ── 2. ASSET & CASH READINESS (0-20) ───────────────────────
  {
    let points = 0;
    const evidence = [];
    const improvements = [];
    const assetsData = appData.assets || {};
    const giftsData = appData.giftsGrants || {};
    const creditsData = appData.credits || {};

    if (assetsData.entries?.length > 0) {
      points += 8;
      evidence.push(`${assetsData.entries.length} asset account${assetsData.entries.length > 1 ? 's' : ''} documented`);
      if (assetsData.entries.length > 2) {
        points += 4;
        evidence.push('Strong asset diversification');
      } else {
        improvements.push({ text: 'Document additional asset accounts (savings, investments, retirement)', impact: 4 });
      }
    } else {
      improvements.push({ text: 'Add bank and investment account details', impact: 12 });
    }

    if (prodRec.downPayment && prodRec.estimatedValue) {
      const dp = parseFloat(prodRec.downPayment);
      const ev = parseFloat(prodRec.estimatedValue);
      if (ev > 0) {
        const pct = (dp / ev) * 100;
        if (pct >= 20) {
          points += 8;
          evidence.push(`Down payment: ${pct.toFixed(0)}% — Eliminates PMI`);
        } else if (pct >= 10) {
          points += 5;
          evidence.push(`Down payment: ${pct.toFixed(0)}%`);
          improvements.push({ text: 'Increasing to 20% down eliminates PMI', impact: 3 });
        } else if (pct >= 3) {
          points += 3;
          evidence.push(`Down payment: ${pct.toFixed(0)}%`);
          improvements.push({ text: 'A larger down payment improves terms and reduces PMI', impact: 5 });
        } else {
          improvements.push({ text: 'Provide a down payment amount', impact: 8 });
        }
      }
    } else if (!prodRec.downPayment) {
      improvements.push({ text: 'Specify your down payment amount', impact: 8 });
    }

    if (giftsData.entries?.length > 0 || creditsData.entries?.length > 0) {
      points += 2;
      evidence.push('Gift funds or credits documented');
    }

    results.push({
      ...CATEGORIES[1],
      points: Math.min(points, 20),
      evidence,
      improvements,
    });
  }

  // ── 3. CREDIT READINESS (0-20) ─────────────────────────────
  {
    let points = 0;
    const evidence = [];
    const improvements = [];
    const raw = prodRec.creditScore || '';

    // Parse credit score — handle range strings like "760+", "700-759"
    let creditScoreNum = 0;
    if (raw.includes('+')) {
      creditScoreNum = parseInt(raw);
    } else if (raw.includes('-')) {
      const parts = raw.split('-');
      creditScoreNum = parseInt(parts[0]);
    } else if (raw.startsWith('<')) {
      creditScoreNum = 570; // below 580 bucket
    } else {
      creditScoreNum = parseInt(raw);
    }

    if (creditScoreNum >= 760) {
      points = 20;
      evidence.push(`Credit score: ${raw} — Excellent`);
    } else if (creditScoreNum >= 720) {
      points = 17;
      evidence.push(`Credit score: ${raw} — Very Good`);
      improvements.push({ text: 'Reaching 760+ unlocks the best rates', impact: 3 });
    } else if (creditScoreNum >= 680) {
      points = 14;
      evidence.push(`Credit score: ${raw} — Good`);
      improvements.push({ text: 'Improving credit score to 720+ would improve your rate tier', impact: 6 });
    } else if (creditScoreNum >= 640) {
      points = 10;
      evidence.push(`Credit score: ${raw} — Fair`);
      improvements.push({ text: 'Focus on credit improvement — paying down balances and on-time payments help most', impact: 10 });
    } else if (creditScoreNum >= 580) {
      points = 6;
      evidence.push(`Credit score: ${raw} — Below Average`);
      improvements.push({ text: 'Credit improvement is the highest-impact action you can take', impact: 14 });
    } else if (creditScoreNum > 0) {
      points = 3;
      evidence.push(`Credit score: ${raw} — Needs Improvement`);
      improvements.push({ text: 'Consider credit counseling — significant improvement is possible', impact: 17 });
    } else {
      improvements.push({ text: 'Provide your estimated credit score', impact: 20 });
    }

    results.push({
      ...CATEGORIES[2],
      points,
      evidence,
      improvements,
    });
  }

  // ── 4. LOAN STRUCTURE FIT (0-15) ───────────────────────────
  {
    let points = 0;
    const evidence = [];
    const improvements = [];
    const propertyData = appData.propertyInfo || {};
    const loanData = appData.loanInfo || {};

    if (Object.keys(propertyData).length > 3) {
      points += 5;
      evidence.push('Property details provided');
    } else {
      improvements.push({ text: 'Complete property information in the full application', impact: 5 });
    }

    if (Object.keys(loanData).length > 2) {
      points += 5;
      evidence.push('Loan details specified');
    } else {
      improvements.push({ text: 'Complete loan details in the full application', impact: 5 });
    }

    if (state.recommendedProduct) {
      points += 5;
      evidence.push(`Matched to: ${state.recommendedProduct.displayName || state.recommendedProduct.name || 'Selected program'}`);
    } else {
      improvements.push({ text: 'Complete the assessment to get matched to a loan program', impact: 5 });
    }

    results.push({
      ...CATEGORIES[3],
      points: Math.min(points, 15),
      evidence,
      improvements,
    });
  }

  // ── 5. DOCUMENTATION STRENGTH (0-10) ───────────────────────
  {
    const documentsCount = (state.documents || []).length;
    const uploadedDocsCount = (state.documentsUploaded || []).length;
    const totalDocs = documentsCount + uploadedDocsCount;
    const points = Math.min(totalDocs * 2, 10);
    const evidence = [];
    const improvements = [];

    if (totalDocs > 0) {
      evidence.push(`${totalDocs} document${totalDocs > 1 ? 's' : ''} uploaded`);
    }

    if (totalDocs < 5) {
      const remaining = 5 - totalDocs;
      improvements.push({ text: `Upload ${remaining} more document${remaining > 1 ? 's' : ''} (pay stubs, bank statements, tax returns)`, impact: Math.min(remaining * 2, 10 - points) });
    }

    results.push({
      ...CATEGORIES[4],
      points,
      evidence,
      improvements,
    });
  }

  // ── 6. MARKET TIMING & INTENT (0-10) ───────────────────────
  {
    let points = 0;
    const evidence = [];
    const improvements = [];

    if (Object.keys(appData.borrowerInfo || {}).length > 3) {
      points += 3;
      evidence.push('Borrower information provided');
    } else {
      improvements.push({ text: 'Complete borrower information in the full application', impact: 3 });
    }

    if (Object.keys(appData.contactInfo || {}).length > 1) {
      points += 2;
      evidence.push('Contact information provided');
    } else {
      improvements.push({ text: 'Add contact information', impact: 2 });
    }

    if (Object.keys(appData.declarations || {}).length > 3) {
      points += 3;
      evidence.push('Declarations completed');
    } else {
      improvements.push({ text: 'Complete declarations section', impact: 3 });
    }

    if (state.underwritingChoice) {
      points += 2;
      evidence.push(`Underwriting path selected: ${state.underwritingChoice === 'ai' ? 'Owen AI' : 'Human underwriter'}`);
    } else {
      improvements.push({ text: 'Choose your underwriting path (AI or Human)', impact: 2 });
    }

    results.push({
      ...CATEGORIES[5],
      points: Math.min(points, 10),
      evidence,
      improvements,
    });
  }

  const total = Math.min(results.reduce((sum, c) => sum + c.points, 0), 100);

  return { total, categories: results };
}

/**
 * Convenience: just the total score (drop-in replacement for the old function).
 */
export function calculateReadinessTotal(state) {
  return calculateReadinessBreakdown(state).total;
}

/** Empty URLA sections — used so assessment scoring ignores persisted full-app data. */
const EMPTY_ASSESSMENT_APPLICATION_DATA = {
  borrowerInfo: { entries: [] },
  contactInfo: {},
  personalInfo: {},
  propertyInfo: {},
  loanInfo: {},
  employmentIncome: { entries: [] },
  otherIncome: { entries: [] },
  assets: { entries: [] },
  credits: { entries: [] },
  giftsGrants: { entries: [] },
  declarations: {},
};

function hasAssessmentAnswers(productRecommendation = {}) {
  return Object.entries(productRecommendation).some(([key, v]) => {
    if (v === '' || v == null) return false;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return !Number.isNaN(v);
    const s = String(v).trim();
    if (!s) return false;
    // Ignore placeholder / sentinel values that are not real borrower answers
    if (key === 'loanSizeTier' && (s === 'unknown' || s === 'unsure')) return false;
    return true;
  });
}

/**
 * Readiness during the conversational assessment only — scores productRecommendation
 * (and program match), not URLA sections left over in local storage.
 */
export function calculateAssessmentReadinessTotal(state) {
  const productRecommendation = state.productRecommendation || {};
  if (!hasAssessmentAnswers(productRecommendation)) {
    return 0;
  }

  return calculateReadinessTotal({
    productRecommendation,
    applicationData: EMPTY_ASSESSMENT_APPLICATION_DATA,
    documents: [],
    documentsUploaded: [],
    recommendedProduct: state.recommendedProduct || null,
    underwritingChoice: null,
  });
}
